import React from 'react'
import { DocumentVersionORM, FolderItemORM } from 'src/types/types'
import {
  useDNADocumentVersionActions,
  documentVersionContextOptions,
  DocumentVersionActions,
} from './DNADocumentVersion.actions'
import { DNAContextMenu } from '@alucio/lux-ui'
import { useAppSettings, useOnlineStatus } from 'src/state/context/AppSettings'
import { DocumentStatus } from '@alucio/aws-beacon-amplify/src/models'
import useCurrentPage from '../hooks/useCurrentPage'

export interface DNADocumentContextMenuProps {
  folderItemORM: FolderItemORM,
  children: React.ReactElement,
  actions?: DocumentVersionActions[],
  unavailableContent?: boolean,
}

/**
 * Start with an empty context menu and push actions based on permissions defined in the document.ts selector
 * @param props folder item / children / actions
 */
export const DNADocumentVersionContextMenu: React.FC<DNADocumentContextMenuProps> = (props) => {
  const {
    folderItemORM,
    actions,
    children,
    unavailableContent,
  } = props

  const { deviceMode } = useAppSettings()
  const DNADocumentVersionActions = useDNADocumentVersionActions()
  const route = useCurrentPage({ exact: false })
  const { isModified } = folderItemORM.meta
  const documentVersionORM = folderItemORM.relations.item as DocumentVersionORM
  const actionsToUse: DocumentVersionActions[] = []
  const isDesktop = deviceMode === 'desktop'
  const onlineStatus = useOnlineStatus()
  const isNetworkConnected = onlineStatus
  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher')
  const isSharedFolders = route?.PATH.includes('shared_folders');
  const canPresent = !unavailableContent && documentVersionORM.relations.document.meta.permissions.MSLPresent;
  const canDownload =
    documentVersionORM.relations.document.meta.permissions.MSLDownload &&
    isNetworkConnected &&
    !isModified
  /**
   * Currently no constraints defined for when a file can be duplicated.
   * This is here to serve as a place holder for when they are and to
   * preserve the existing coding pattern.
   * */
  const canDuplicate = true
  const canShare =
    documentVersionORM.meta.permissions.MSLShare &&
    !folderItemORM.model.customTitle &&
    !folderItemORM.model.visiblePages?.length &&
    isNetworkConnected &&
    !isModified &&
    !isPublisherRoute
  const canSelectSlides = documentVersionORM.relations.document.meta.permissions.MSLSelectSlides &&
    !isPublisherRoute
  const canDownloadPdfFile = false // TODO: REACTIVATE THIS IN A FUTURE STORY isNetworkConnected && documentVersionORM.relations.document.model.type === FileType.PPTX

  // NOTE: We're assuming this is only used in an MSL related context
  canPresent && actionsToUse.push('present');
  canDownload && actionsToUse.push('download')
  canDownloadPdfFile && actionsToUse.push('downloadAsPDF');
  canDuplicate && !isSharedFolders && actionsToUse.push('duplicate')
  canShare && actionsToUse.push('shareEMail', 'copyShareLink')
  /** Currently INT files can not use selectSlides option. Additionally,
   * docs have a property which enables this functionality */
  canSelectSlides && !isSharedFolders && actionsToUse.push('selectSlides');

  // Add to folder for Folder view
  const isArchived: boolean =
    [DocumentStatus.ARCHIVED, DocumentStatus.ARCHIVED_WITH_VERSIONS].includes(
      DocumentStatus[documentVersionORM.relations.document.model.status],
    );
  !isArchived && actionsToUse.push('addToFolder');

  // This context menu option is only used in MyFolders currently
  !isSharedFolders && actionsToUse.push('removeFromFolder');

  const MenuItems = actions?.length || actionsToUse.length
    ? (actions ?? actionsToUse)
      .map(action => {
        const { onPress, ...rest } = documentVersionContextOptions[action]
        return (
          <DNAContextMenu.Item
            disabled={ actions ? !actions.length : !actionsToUse.length}
            key={action}
            status={isDesktop ? 'neutral' : 'dark'}
            onPress={DNADocumentVersionActions[action](documentVersionORM, folderItemORM)}
            {...rest}
          />
        )
      })
    : (<DNAContextMenu.Item
        title="(No actions available)"
        onPress={() => {}}
    />)

  return (
    <DNAContextMenu>
      <DNAContextMenu.Anchor>
        { children }
      </DNAContextMenu.Anchor>
      <DNAContextMenu.Items>
        { MenuItems }
      </DNAContextMenu.Items>
    </DNAContextMenu>
  )
}

export default DNADocumentVersionContextMenu
